/**
 * TypeScript interfaces mirroring the Pydantic schemas for the KPI Scorecard.
 * Wire convention: snake_case throughout (matches the backend).
 * Source of truth: docs/superpowers/specs/2026-06-23-pm-kpi-scorecard-design.md § 6
 *   (base scorecard) extended by
 * docs/superpowers/specs/2026-07-02-scorecard-reliability-design.md § 6
 *   (reliability gating, ramping, readiness view, "Since 14 Jun" preset)
 */

export type KpiStatus =
  | 'green'
  | 'amber'
  | 'red'
  | 'insufficient_data'
  | 'not_available'
  | 'not_tracked'
  | 'na';

/** The three "gated out" / neutral statuses — distinct from RAG and from `na`. */
export const NEUTRAL_STATUSES: readonly KpiStatus[] = [
  'insufficient_data',
  'not_available',
  'not_tracked',
];

export function isNeutralStatus(status: KpiStatus): boolean {
  return (NEUTRAL_STATUSES as readonly string[]).includes(status);
}

export type DeltaKind = 'good' | 'bad' | 'flat';
export type PeriodPreset = 'today' | 'week' | 'month' | 'qtd' | 'since_cutoff' | 'custom';

export interface ScorecardPeriodWindow {
  start: string; // ISO date
  end: string;   // ISO date
  label: string;
}

export interface ScorecardPeriod {
  preset: PeriodPreset;
  start: string;  // ISO date
  end: string;    // ISO date
  label: string;
  prior: ScorecardPeriodWindow;
}

export interface KpiTarget {
  op: string;           // '>=' | '<=' | '<' | '==' | 'reduce'
  value: number;
  label: string;
  prorated: number | null;
}

export interface KpiDelta {
  value: number | null;
  display: string;
  kind: DeltaKind;
}

/** Reliability gate metadata — surfaced on every Metric per the reliability design spec. */
export interface MetricReliability {
  measured: number;
  population: number;
  coverage_pct: number | null;
  min_n: number;
  gated: boolean;
  gate_reason: string | null;
}

export interface Metric {
  key: string;
  label: string;
  group: string;
  unit: string | null;
  value: number | null;
  display: string;
  target: KpiTarget;
  status: KpiStatus;
  na_reason: string | null;
  delta: KpiDelta;
  spark: number[];
  definition: string;
  source: string;
  reliability: MetricReliability;
  ramping: boolean;
}

export interface ScorecardGroup {
  key: string;
  label: string;
  metrics: Metric[];
}

export interface ScorecardSummary {
  green: number;
  amber: number;
  red: number;
  na: number;
  /** Umbrella count for insufficient_data + not_available + not_tracked (gated-out, neutral). */
  insufficient: number;
  total: number;
  /** Reflects only gated-in (RAG) metrics — insufficient/not_available/not_tracked are excluded. */
  verdict: string;
}

export interface ScorecardResponse {
  period: ScorecardPeriod;
  generated_at: string; // ISO datetime
  last_synced_at: string | null; // most recent Notion snapshot_at (data freshness), null when never synced
  summary: ScorecardSummary;
  groups: ScorecardGroup[];
}

// ---- Manual sync (admin-only) ----

export interface ScorecardSyncResult {
  tasks_synced: number;
  tickets_synced: number;
  transitions_recorded: number;
  took_ms: number;
  last_synced_at: string | null;
}

// ---- Metric detail (drawer) ----

export interface Bucket {
  key: string;
  count: number;
  dev_days: number;
}

export interface ScorecardBreakdowns {
  size: Bucket[];
  pipeline: Bucket[];
  owner: Bucket[];
  project: Bucket[];
  type: Bucket[];
  severity: Bucket[];
}

export interface ScorecardTask {
  notion_id: string;
  title: string;
  size: string | null;
  stage: string;
  owner: string | null;
  project: string | null;
  dev_days: number | null;
  value: string | null;
  notion_url: string | null;
}

export interface TrendPoint {
  period: string;
  value: number | null;
}

export interface ScorecardMetricDetail {
  metric: Metric;
  breakdowns: ScorecardBreakdowns;
  tasks: ScorecardTask[];
  trend: {
    qoq: TrendPoint[];
    wow: TrendPoint[];
  };
}

// ---- Period params sent to the API ----

export interface ScorecardParams {
  preset?: PeriodPreset;
  start?: string; // ISO date, required when preset='custom'
  end?: string;   // ISO date, required when preset='custom'
}

// ---- Readiness view (backfill checklist) ----
// Mirrors GET /api/pm/scorecard/readiness/ — see
// docs/superpowers/specs/2026-07-02-scorecard-reliability-design.md § 6

export interface ReadinessCohort {
  size: number;
  cutoff: string | null; // ISO date; null when scorecard_cutoff_date is disabled
  in_scope: number;
  excluded_no_project: number;
}

export interface ReadinessField {
  field: string;
  label: string;
  present: number;
  total: number;
  coverage_pct: number;
  feeds: string[];
}

export interface ReadinessTask {
  notion_id: string;
  title: string;
  project: string | null;
  stage: string;
  missing: string[];
  present: string[];
  notion_url: string | null;
}

export interface ScorecardReadiness {
  cohort: ReadinessCohort;
  fields: ReadinessField[];
  tasks: ReadinessTask[];
}

export interface ScorecardReadinessParams {
  preset?: PeriodPreset;
  start?: string; // ISO date
  end?: string;   // ISO date
  project?: string;
}
